import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Button, Fade, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Ramp } from "../../../../../../../types";
import { GPSEntryPopover } from "../components/GPSEntryPopover";
import { useSetupWizardStore } from "../useSetupWizardStore";

type Props = {
  ramp: Ramp;
  setRamp: (updater: (prev: Ramp) => Ramp) => void;
};

export const CalibrationStep: React.FC<Props> = ({ ramp: _ramp, setRamp: _setRamp }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    pendingPoints,
    editingPointIndex,
    popoverPosition,
    nextStep,
    setEditingPoint,
    updatePointGPS,
    removePoint,
    getCompletedPoints,
  } = useSetupWizardStore();

  const completedPoints = getCompletedPoints();
  const hasEnoughPoints = completedPoints.length >= 3;

  // Handle clicking on a progress dot to edit that point
  const handleDotClick = (e: React.MouseEvent, index: number) => {
    const point = pendingPoints[index];
    if (point) {
      const rect = e.currentTarget.getBoundingClientRect();
      setEditingPoint(index, { x: rect.left + rect.width / 2, y: rect.top });
    }
  };

  // Determine instruction based on state
  const getInstruction = () => {
    const placedCount = pendingPoints.length;
    const completedCount = completedPoints.length;

    // If we have a point waiting for GPS
    const pendingGPS = pendingPoints.find(
      (p) => p.latitude === undefined || p.longitude === undefined
    );
    if (pendingGPS) {
      return t(
        "settings.rampsSettings.setupWizard.calibrate.enterGPS",
        "Enter GPS for point {{label}}",
        { label: pendingGPS.label }
      );
    }

    if (placedCount < 3) {
      const labels = ["A", "B", "C"];
      return t(
        "settings.rampsSettings.setupWizard.calibrate.clickToPlace",
        "Click to place point {{label}}",
        { label: labels[placedCount] }
      );
    }

    if (completedCount >= 3) {
      return t(
        "settings.rampsSettings.setupWizard.calibrate.ready",
        "Calibration complete — click any point to edit"
      );
    }

    return "";
  };

  const handleClosePopover = () => {
    setEditingPoint(null);
  };

  const handleSaveGPS = (latitude: number, longitude: number) => {
    if (editingPointIndex !== null) {
      updatePointGPS(editingPointIndex, latitude, longitude);
    }
  };

  const handleDeletePoint = () => {
    if (editingPointIndex !== null) {
      removePoint(editingPointIndex);
    }
  };

  return (
    <>
      {/* Bottom center instruction badge */}
      <Fade in timeout={300}>
        <Box
          sx={{
            position: "absolute",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              backgroundColor: "background.paper",
              borderRadius: 3,
              px: 3,
              py: 1.5,
              boxShadow: 4,
            }}
          >
            {/* Progress dots */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {["A", "B", "C"].map((label, index) => {
                const point = pendingPoints[index];
                const hasGPS =
                  point?.latitude !== undefined && point?.longitude !== undefined;
                const isPlaced = Boolean(point);

                return (
                  <Box
                    key={label}
                    onClick={(e) => handleDotClick(e, index)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: hasGPS
                        ? theme.palette.success.main
                        : isPlaced
                        ? theme.palette.warning.main
                        : theme.palette.action.disabledBackground,
                      color: hasGPS || isPlaced ? "white" : theme.palette.text.disabled,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      transition: "all 0.15s ease",
                      cursor: isPlaced ? "pointer" : "default",
                      "&:hover": isPlaced
                        ? {
                            transform: "scale(1.15)",
                            boxShadow: 2,
                          }
                        : {},
                    }}
                  >
                    {hasGPS ? <CheckIcon sx={{ fontSize: 16 }} /> : label}
                  </Box>
                );
              })}
            </Box>

            {/* Instruction text */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: hasEnoughPoints
                  ? theme.palette.success.main
                  : theme.palette.text.primary,
              }}
            >
              {getInstruction()}
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* Floating Next button - appears when ready */}
      <Fade in={hasEnoughPoints} timeout={300}>
        <Box
          sx={{
            position: "absolute",
            bottom: 24,
            right: 24,
            pointerEvents: hasEnoughPoints ? "auto" : "none",
          }}
        >
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={nextStep}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              boxShadow: 4,
              textTransform: "none",
            }}
          >
            {t("common.next", "Next")}
          </Button>
        </Box>
      </Fade>

      {/* GPS Entry Popover - appears near clicked point */}
      {editingPointIndex !== null && popoverPosition && (
        <GPSEntryPopover
          open
          anchorPosition={popoverPosition}
          pointLabel={pendingPoints[editingPointIndex]?.label || ""}
          initialLatitude={pendingPoints[editingPointIndex]?.latitude}
          initialLongitude={pendingPoints[editingPointIndex]?.longitude}
          onSave={handleSaveGPS}
          onClose={handleClosePopover}
          onDelete={handleDeletePoint}
        />
      )}
    </>
  );
};
